/**
 * Kontakthistorie in der Partnerakte (Fachmodul "crm").
 *
 * Zeigt die Aktivitaeten des Partners unter den Stammdaten - die Antwort
 * auf "was wurde zuletzt mit dem Kunden besprochen", ohne die Seite zu
 * wechseln. Neue Eintraege entstehen direkt hier; der Aussendienst des
 * Kunden ist bei Wiedervorlagen vorbelegt.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { formatDate } from '@erp/shared';

import { api, withQuery } from '../api/client';
import type { ListResponse } from '../api/types';
import { useAuth } from '../auth';
import {
  ActivityDetailDialog,
  CreateActivityDialog,
  dueLabel,
  kindBadge,
  type ActivityRow,
} from './CrmActivity';
import { Badge, CollapsibleTitle, EmptyState, LoadingBlock, useCollapsed } from './ui';

export function PartnerActivities({
  partnerId,
  partnerLabel,
  salesRepId,
}: {
  partnerId: string;
  partnerLabel: string;
  salesRepId: string | null;
}) {
  const { can } = useAuth();
  const [creating, setCreating] = useState(false);
  const [openRow, setOpenRow] = useState<ActivityRow | null>(null);
  const [collapsed, toggle] = useCollapsed('kontakthistorie');

  const { data, isLoading } = useQuery({
    queryKey: ['crm-activities', 'partner', partnerId],
    queryFn: () =>
      api.get<ListResponse<ActivityRow>>(
        withQuery('/api/crm/activities', { partnerId, limit: 100 }),
      ),
  });

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div className="card-body">
        <div className="row" style={{ justifyContent: 'space-between', marginBottom: collapsed ? 0 : 10 }}>
          <CollapsibleTitle title="Kontakthistorie" collapsed={collapsed} onToggle={toggle} />
          {can('crm:write') && !collapsed ? (
            <button type="button" className="btn btn-sm" onClick={() => setCreating(true)}>
              Aktivität erfassen
            </button>
          ) : null}
        </div>

        {collapsed ? null : isLoading || !data ? (
          <LoadingBlock />
        ) : data.items.length === 0 ? (
          <EmptyState title="Noch keine Aktivitäten zu diesem Partner." />
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th className="shrink">Datum</th>
                  <th className="shrink">Art</th>
                  <th>Betreff</th>
                  <th>Zuständig</th>
                  <th className="shrink">Wiedervorlage</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((row) => (
                  <tr key={row.id} onClick={() => setOpenRow(row)} style={{ cursor: 'pointer' }}>
                    <td>{formatDate(row.activityDate)}</td>
                    <td>{kindBadge(row.kind)}</td>
                    <td>{row.subject}</td>
                    <td>{row.assignedToName ?? <span className="faint">—</span>}</td>
                    <td>
                      {row.status === 'open' ? (
                        dueLabel(row)
                      ) : row.dueOn ? (
                        <Badge kind="success">Erledigt</Badge>
                      ) : (
                        <span className="faint">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {creating ? (
        <CreateActivityDialog
          partnerId={partnerId}
          partnerLabel={partnerLabel}
          defaultAssignedTo={salesRepId}
          onClose={() => setCreating(false)}
        />
      ) : null}
      {openRow ? <ActivityDetailDialog activity={openRow} onClose={() => setOpenRow(null)} /> : null}
    </div>
  );
}
